export type LocaleId = 'en' | 'pt-BR' | 'es';

export const LOCALES: readonly LocaleId[] = ['en', 'pt-BR', 'es'];
export const LOCALE_STORAGE_KEY = 'park_v1_ui_locale';

function isLocale(value: string | null): value is LocaleId {
  return value === 'en' || value === 'pt-BR' || value === 'es';
}

export function detectLocale(language = typeof navigator === 'undefined' ? 'en' : navigator.language): LocaleId {
  const lower = language.toLowerCase();
  if (lower.startsWith('pt')) {
    return 'pt-BR';
  }
  if (lower.startsWith('es')) {
    return 'es';
  }
  return 'en';
}

let current: LocaleId = 'en';
const listeners = new Set<() => void>();

export function getLocale(): LocaleId {
  return current;
}

export function setLocale(locale: LocaleId): void {
  if (current === locale) {
    return;
  }
  current = locale;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore missing Storage in tests / private contexts.
  }
  for (const listener of listeners) {
    listener();
  }
}

export function cycleLocale(): LocaleId {
  const index = LOCALES.indexOf(current);
  const next = LOCALES[(index + 1) % LOCALES.length]!;
  setLocale(next);
  return next;
}

export function initLocale(): LocaleId {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) {
      current = stored;
      return current;
    }
  } catch {
    // Ignore missing Storage.
  }
  current = 'en';
  return current;
}

export function onLocaleChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function localeShort(locale: LocaleId = current): string {
  if (locale === 'pt-BR') {
    return 'PT';
  }
  if (locale === 'es') {
    return 'ES';
  }
  return 'EN';
}
